// Authentication Provider
// Wraps the app and provides authentication state

import { User as FirebaseUser, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import {
    canUseLocalImpersonation,
    clearStoredLocalImpersonation,
    fetchLocalOrganizationUsers,
    fetchLocalOrganizations,
    getStoredLocalOrganizationId,
    getStoredLocalRole,
    getStoredLocalUserId,
    setActiveDevOrgIdForApi,
    setStoredLocalOrganizationId,
    setStoredLocalRole,
    setStoredLocalUserId,
} from '../dev/localImpersonation';
import { getAuth, getDB } from '../firebase/config';
import type { Organization, User } from '../models';
import { AuthContext } from './AuthContext';

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const localImpersonationAvailable = canUseLocalImpersonation(firebaseUser?.email);
  const [user, setUser] = useState<User | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [availableOrganizations, setAvailableOrganizations] = useState<Organization[]>([]);
  const [availableLocalUsers, setAvailableLocalUsers] = useState<User[]>([]);
  const [selectedOrganizationId, setSelectedOrganizationIdState] = useState<string | null>(() => getStoredLocalOrganizationId());
  const [selectedLocalUserId, setSelectedLocalUserIdState] = useState<string | null>(() => getStoredLocalUserId());
  const [selectedLocalRole, setSelectedLocalRoleState] = useState<User['role']>(() => getStoredLocalRole() ?? 'admin');
  const [localLoading, setLocalLoading] = useState(localImpersonationAvailable);
  const [localError, setLocalError] = useState<string | null>(null);
  const [localRefreshVersion, setLocalRefreshVersion] = useState(0);

  useLayoutEffect(() => {
    if (localImpersonationAvailable && selectedOrganizationId) {
      setActiveDevOrgIdForApi(selectedOrganizationId);
    } else {
      setActiveDevOrgIdForApi(null);
    }
  }, [localImpersonationAvailable, selectedOrganizationId]);

  useEffect(() => {
    if (!localImpersonationAvailable) {
      clearStoredLocalImpersonation();
      setSelectedOrganizationIdState(null);
      setSelectedLocalUserIdState(null);
      setSelectedLocalRoleState('admin');
      setAvailableOrganizations([]);
      setAvailableLocalUsers([]);
      setLocalLoading(false);
      setLocalError(null);
      return;
    }

    let isCancelled = false;

    const loadOrganizations = async () => {
      try {
        setLocalLoading(true);
        const organizations = await fetchLocalOrganizations(firebaseUser?.email);
        if (isCancelled) {
          return;
        }

        setAvailableOrganizations(organizations);
        setLocalError(null);

        if (selectedOrganizationId && !organizations.some((org) => org.id === selectedOrganizationId)) {
          setStoredLocalOrganizationId(null);
          setSelectedOrganizationIdState(null);
        }
      } catch (err) {
        if (isCancelled) {
          return;
        }

        console.warn('Local impersonation disabled:', err);
        setAvailableOrganizations([]);
        setLocalError((err as Error).message);
      } finally {
        if (!isCancelled) {
          setLocalLoading(false);
        }
      }
    };

    loadOrganizations();

    return () => {
      isCancelled = true;
    };
  }, [localImpersonationAvailable, selectedOrganizationId, localRefreshVersion, firebaseUser?.email]);

  const setSelectedOrganizationId = (organizationId: string | null) => {
    setStoredLocalOrganizationId(organizationId, firebaseUser?.email);
    setSelectedOrganizationIdState(organizationId);

    if (!organizationId) {
      setStoredLocalUserId(null, firebaseUser?.email);
      setSelectedLocalUserIdState(null);
      setStoredLocalRole(null, firebaseUser?.email);
      setSelectedLocalRoleState('admin');
      setAvailableLocalUsers([]);
      return;
    }

    setStoredLocalUserId(null, firebaseUser?.email);
    setSelectedLocalUserIdState(null);
    setStoredLocalRole(null, firebaseUser?.email);
    setSelectedLocalRoleState('admin');
  };

  const setSelectedLocalUserId = (userId: string | null) => {
    setStoredLocalUserId(userId, firebaseUser?.email);
    setSelectedLocalUserIdState(userId);

    if (!userId) {
      return;
    }

    const matchingUser = availableLocalUsers.find((candidate) => candidate.id === userId);
    if (matchingUser?.role) {
      setStoredLocalRole(matchingUser.role, firebaseUser?.email);
      setSelectedLocalRoleState(matchingUser.role);
    }
  };

  const setSelectedLocalRole = (role: User['role']) => {
    setStoredLocalRole(role, firebaseUser?.email);
    setSelectedLocalRoleState(role);
  };

  const refreshLocalImpersonationData = () => {
    setLocalRefreshVersion((current) => current + 1);
  };

  useEffect(() => {
    if (!localImpersonationAvailable || !selectedOrganizationId) {
      setAvailableLocalUsers([]);
      return;
    }

    let isCancelled = false;

    const loadUsers = async () => {
      try {
        setLocalLoading(true);
        const users = await fetchLocalOrganizationUsers(selectedOrganizationId, firebaseUser?.email);
        if (isCancelled) {
          return;
        }

        setAvailableLocalUsers(users);
        setLocalError(null);

        const matchingSelectedUser = selectedLocalUserId
          ? users.find((candidate) => candidate.id === selectedLocalUserId)
          : null;

        if (matchingSelectedUser) {
          return;
        }

        const fallbackUser = users[0] ?? null;
        if (fallbackUser) {
          setStoredLocalUserId(fallbackUser.id, firebaseUser?.email);
          setSelectedLocalUserIdState(fallbackUser.id);
          setStoredLocalRole(fallbackUser.role, firebaseUser?.email);
          setSelectedLocalRoleState(fallbackUser.role);
          return;
        }

        setStoredLocalUserId(null, firebaseUser?.email);
        setSelectedLocalUserIdState(null);
      } catch (err) {
        if (isCancelled) {
          return;
        }

        console.warn('Unable to load local impersonation users:', err);
        setAvailableLocalUsers([]);
        setLocalError((err as Error).message);
      } finally {
        if (!isCancelled) {
          setLocalLoading(false);
        }
      }
    };

    loadUsers();

    return () => {
      isCancelled = true;
    };
  }, [localImpersonationAvailable, selectedOrganizationId, selectedLocalUserId, localRefreshVersion, firebaseUser?.email]);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribeAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      setFirebaseUser(firebaseUser);
      setLoading(true);
      setError(null);

      if (firebaseUser) {
        try {
          // Load user document from Firestore
          const db = getDB();
          const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));

          if (userDoc.exists()) {
            const userData = { id: userDoc.id, ...userDoc.data() } as User;
            setUser(userData);

            // Load organization
            if (userData.organizationId) {
              const orgDoc = await getDoc(doc(db, 'organizations', userData.organizationId));
              if (orgDoc.exists()) {
                const orgData = { id: orgDoc.id, ...orgDoc.data() } as Organization;
                setOrganization(orgData);
              }
            }
          } else {
            // User document doesn't exist yet (might be during signup)
            setUser(null);
            setOrganization(null);
          }
        } catch (err) {
          console.error('Error loading user data:', err);
          setError((err as Error).message);
          setUser(null);
          setOrganization(null);
        }
      } else {
        setUser(null);
        setOrganization(null);
      }

      setLoading(false);
    });

    return () => unsubscribeAuth();
  }, []);

  // Set up real-time listener for user document
  useEffect(() => {
    if (!firebaseUser) {
      setUser(null);
      setOrganization(null);
      return;
    }

    const db = getDB();
    const unsubscribeUser = onSnapshot(
      doc(db, 'users', firebaseUser.uid),
      async (userDoc) => {
        if (userDoc.exists()) {
          const userData = { id: userDoc.id, ...userDoc.data() } as User;
          setUser(userData);

          // Load organization if it changed
          if (userData.organizationId) {
            const orgDoc = await getDoc(doc(db, 'organizations', userData.organizationId));
            if (orgDoc.exists()) {
              const orgData = { id: orgDoc.id, ...orgDoc.data() } as Organization;
              setOrganization(orgData);
            }
          } else {
            setOrganization(null);
          }
        } else {
          setUser(null);
          setOrganization(null);
        }
      },
      (err) => {
        console.error('Error in user listener:', err);
        setError((err as Error).message);
      }
    );

    return () => unsubscribeUser();
  }, [firebaseUser]);

  // Listen for organization updates so settings stay in sync in the UI
  useEffect(() => {
    if (!user?.organizationId) {
      setOrganization(null);
      return;
    }

    const db = getDB();
    const unsubscribeOrg = onSnapshot(
      doc(db, 'organizations', user.organizationId),
      (orgDoc) => {
        if (orgDoc.exists()) {
          const orgData = { id: orgDoc.id, ...orgDoc.data() } as Organization;
          setOrganization(orgData);
        } else {
          setOrganization(null);
        }
      },
      (err) => {
        console.error('Error in organization listener:', err);
        setError((err as Error).message);
      }
    );

    return () => unsubscribeOrg();
  }, [user?.organizationId]);

  const impersonatedOrganizationFromList = selectedOrganizationId
    ? availableOrganizations.find((candidate) => candidate.id === selectedOrganizationId) ?? null
    : null;

  const impersonatedSourceUser = selectedLocalUserId
    ? availableLocalUsers.find((candidate) => candidate.id === selectedLocalUserId) ?? null
    : null;

  // Key off selectedOrganizationId, not the org list lookup, so we impersonate immediately
  // (otherwise Firestore listeners run against the superuser's real org until the list loads).
  const isLocalImpersonation = localImpersonationAvailable && !!selectedOrganizationId;

  const impersonatedUser: User | null = isLocalImpersonation
    ? {
        id: impersonatedSourceUser?.id ?? `local-dev:${selectedOrganizationId}`,
        email: impersonatedSourceUser?.email ?? 'local-dev@localhost',
        displayName: impersonatedSourceUser?.displayName ?? 'Local Developer',
        organizationId: selectedOrganizationId,
        role: selectedLocalRole,
        createdAt: impersonatedSourceUser?.createdAt,
        updatedAt: impersonatedSourceUser?.updatedAt,
      }
    : null;

  const impersonatedOrganizationResolved: Organization | null = isLocalImpersonation
    ? impersonatedOrganizationFromList ?? {
        id: selectedOrganizationId,
        name: 'Loading organization…',
      }
    : null;
  const effectiveFirebaseUser = isLocalImpersonation ? null : firebaseUser;
  const effectiveUser = isLocalImpersonation ? impersonatedUser : user;
  const effectiveOrganization = isLocalImpersonation ? impersonatedOrganizationResolved : organization;
  const effectiveLoading = loading || (localImpersonationAvailable && localLoading);
  const effectiveError = error || (!firebaseUser && !user ? localError : null);

  const value = {
    firebaseUser: effectiveFirebaseUser,
    user: effectiveUser,
    organization: effectiveOrganization,
    loading: effectiveLoading,
    error: effectiveError,
    isAuthenticated: isLocalImpersonation ? true : !!firebaseUser && !!user,
    isAdmin: isLocalImpersonation ? selectedLocalRole === 'admin' : effectiveUser?.role === 'admin',
    organizationId: effectiveUser?.organizationId || null,
    canUseLocalImpersonation: localImpersonationAvailable,
    isLocalImpersonation,
    availableOrganizations,
    availableLocalUsers,
    selectedOrganizationId,
    setSelectedOrganizationId,
    selectedLocalUserId,
    setSelectedLocalUserId,
    selectedLocalRole,
    setSelectedLocalRole,
    refreshLocalImpersonationData,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
